import {
	Component,
	computed,
	effect,
	inject,
	OnInit,
	OnDestroy,
	signal,
	ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { VbrickPlayerModule } from '@vbrick/vbrick-player';
import { PLAYER_TRANSLATIONS } from './PlayerTestPageTranslations';
import { StreamData } from '../../shared/stream.token';
import { VideogularComponent } from '@vbrick/vbrick-player';
import { RaceSessionService } from '../../timing-tower/race-session.service';
import { ScriptLoaderService } from '../../shared/script-loader.service';
import { FeatureSettingsService } from '../../shared/feature-settings.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { VisibilitiesService } from '../../shared/visibilities.service';
import { TelemetryService } from '../../timing-tower/telemetry.service';
import { ITelemetryData } from '../../timing-tower/ITelemetryData';
import { BarGaugeComponent } from '../../timing-tower/bar-gauge/bar-gauge.component';
import { delay } from 'rxjs';

let playerId = 0;

@Component({
	selector: 'vb-lxp-video-player',
	standalone: true,
	imports: [CommonModule, VbrickPlayerModule, BarGaugeComponent],
	templateUrl: './vb-lxp-video-player.component.html',
	styleUrl: './vb-lxp-video-player.component.css',
})
export class VbLxpVideoPlayerComponent implements OnInit, OnDestroy {
	public readonly PLAYER_TRANSLATIONS = PLAYER_TRANSLATIONS;

	public vgAPI!: VideogularComponent;

	public featureService = inject(FeatureSettingsService);
	public scriptLoaderService = inject(ScriptLoaderService);
	public raceSessionService = inject(RaceSessionService);
	public visibilitiesService = inject(VisibilitiesService);

	private cdr = inject(ChangeDetectorRef);

	public telemetry = signal<ITelemetryData | null>(null);
	public showTelemetry = signal(false);

	public telemetryService = inject(TelemetryService);

	public index = 0;
	public raceHappening = computed(() => this.raceSessionService.raceHappening());
	private readonly isMockDataType =
		(new URLSearchParams(window.location.search).get('ldatatype') || '').toLowerCase() === 'mock';

	public featureSettings = toSignal(this.featureService.featureSettings$);
	public raceStreamDisabled = computed(() => this.featureSettings()?.race.disableStream);
	public playerId: number;
	public twitchContainerId = '';
	private readonly streamDataVersion = signal(0);
	private previousRaceHappening: boolean | null = null;
	private _streamData: StreamData | null = null;
	public get streamData(): StreamData | null {
		return this._streamData;
	}
	public set streamData(value: StreamData | null) {
		this._streamData = value;
		this.streamDataVersion.update((version) => version + 1);
	}

	public getStreamDataVersion() {
		return this.streamDataVersion();
	}
	public isMainPlayer = false;
	public isTwitchPlayer = false;
	public twitchPlayer: any;
	public twitchPlayerLoaded = false;
	public vgApi: VideogularComponent;

	// URL Retry mechanism properties
	private retryIntervalId: any = null;
	private readonly RETRY_INTERVAL_MS = 2000; // 5 seconds
	private retryAttempts = 0;
	private isComponentDestroyed = false;
	public isRetryingUrl = signal(false);
	public showStreamUnavailableMessage = signal(false);
	private readonly raceHappeningEffect = effect(() => {
		const currentRaceHappening = this.raceHappening();
		if (
			this.previousRaceHappening !== currentRaceHappening &&
			this.isMainPlayer &&
			this.streamData
		) {
			// Update previous state
			this.previousRaceHappening = currentRaceHappening;

			// Force reconfiguration with retry mechanism
			this.streamPlayerConfig = null;
			setTimeout(() => {
				this.setupStreamWithRetry(this.streamData);
			}, 100);
		}
	});

	public streamPlayerConfig = {
		sources: [
			{
				is360Enabled: false,
				isLive: true,
				label: `Main Feed`,
				qValue: 2800,
				src: '',
				type: 'HLS',
			},
		],
		autoPlay: true,
		isLive: true,
		playerConfig: {
			playerControls: {
				settings: false,
				hideMuteButton: true,
			},
		},
	};

	// Video stream has roughly 10 second delay so offset telemetry by that much to match
	private readonly MESSAGE_DELAY = 18000;

	public ngOnInit(): void {
		this.playerId = ++playerId;
		this.twitchContainerId = `twitch-player-container-${this.playerId}`;
		if (this.visibilitiesService.controlVisibilities().showTelemetry) {
			this.telemetryService.telemetryData$.pipe(delay(this.MESSAGE_DELAY)).subscribe((data) => {
				const telem = data.find((d) => d.scoring.number === this.streamData?.carNumber);
				this.telemetry.set(telem);
			});
		}
	}

	public ngOnDestroy(): void {
		this.isComponentDestroyed = true;
		this.stopUrlRetry();
	}

	public disablePlayback() {
		this.stopUrlRetry();
		this.streamPlayerConfig = null;
		this.showStreamUnavailableMessage.set(false);
		if (this.streamData) {
			this.streamData.hlsPlaybackUrl = '';
		}
	}

	public toggleTelemetry() {
		this.showTelemetry.update((t) => !t);
	}

	public setStreamData(streamData: StreamData) {
		// Stop any existing retry for the previous stream
		this.stopUrlRetry();

		this.streamData = streamData;
		this.isMainPlayer = streamData?.id?.toLowerCase().startsWith('main-stream') || false;
		this.isTwitchPlayer =
			streamData?.id === 'twitch-stream' || streamData?.streamingPartner === 'Twitch';
		this.twitchPlayerLoaded = false;
		this.previousRaceHappening = this.raceHappening();

		if (this.isTwitchPlayer) {
			this.setTwitchStream();
			return;
		}

		// Try to set up the stream with retry mechanism
		this.setupStreamWithRetry(this.streamData);
	}

	public setStreamPlayerConfig(streamData: StreamData) {
		const playbackData = this.resolveStreamSourceUrl(streamData);
		const currentUrl = this.streamPlayerConfig?.sources?.[0]?.src;

		// If the slot is re-selected with the same source, resume playback to avoid stuck promo video.
		if (currentUrl === playbackData.url) {
			setTimeout(() => {
				this.vgAPI?.play();
			}, 50);
			return;
		}

		this.streamPlayerConfig = {
			sources: [
				{
					is360Enabled: false,
					isLive: playbackData.isLive,
					label: `${streamData?.teamName} - Car #${streamData?.carNumber}`,
					qValue: 2800,
					src: playbackData.url,
					type: 'HLS',
				},
			],
			autoPlay: true,
			isLive: playbackData.isLive,
			playerConfig: {
				playerControls: {
					settings: false,
					hideMuteButton: true,
				},
			},
		};

		this.cdr.detectChanges();
		setTimeout(() => {
			this.vgAPI?.play();
		}, 50);
	}
	public enablePlayback(url: string) {
		if (!this.streamData) {
			return;
		}

		this.streamData.hlsPlaybackUrl = url;
		// Use retry mechanism to ensure URL is available
		this.setupStreamWithRetry(this.streamData);
	}

	public onPlaybackEnded(): void {
		if (this.vgAPI?.activeSource?.src === this.streamData?.hlsPromotionalVideoUrl) {
			setTimeout(() => {
				this.vgAPI?.play();
			}, 100);
		}
	}

	private resolveStreamSourceUrl(streamData: StreamData): { url: string; isLive: boolean } {
		let playbackData;
		if (!this.isMainPlayer) {
			playbackData = {
				url: streamData?.hlsPlaybackUrl,
				isLive: streamData?.isLive ?? true,
			};
		} else if (
			streamData?.hlsPromotionalVideoUrl &&
			(this.isMockDataType || !this.raceHappening())
		) {
			playbackData = {
				url: streamData?.hlsPromotionalVideoUrl,
				isLive: false,
			};
		} else {
			playbackData = {
				url: streamData?.hlsPlaybackUrl,
				isLive: true,
			};
		}
		return playbackData;
	}

	public onPlayerReady(vgAPI: VideogularComponent): void {
		this.vgAPI = vgAPI;
		setTimeout(() => {
			if (!this.isMainPlayer) {
				this.vgAPI.setVolume(0);
			}
		}, 100);
	}

	public onPlaybackError(): void {
		console.warn('Playback error occurred, initiating retry mechanism');
		if (this.streamData) {
			// Trigger retry flow with fallback to promotional video or message
			this.setupStreamWithRetry(this.streamData);
		}
	}

	private loadTwitchPlayer() {
		setTimeout(() => {
			this.scriptLoaderService.loadScript('https://player.twitch.tv/js/embed/v1.js').then(() => {
				this.playTwitchStream();
			});
		}, 200);
	}

	public setTwitchStream() {
		this.loadTwitchPlayer();
	}

	private playTwitchStream() {
		try {
			const options = {
				width: '100%',
				height: '100%',
				allowfullscreen: true,
				channel: 'sromotorsports',
				parent: [window.location.hostname],
				autoplay: true,
			};
			this.twitchPlayer = new (window as any).Twitch.Player(this.twitchContainerId, options);
			this.twitchPlayerLoaded = true;
		} catch (error) {
			console.error('Error loading Twitch player:', error);
		}
	}

	public formatSpeed(speed: number) {
		return Math.round(speed * 0.621371) + 'mph';
	}

	/**
	 * Sets up the stream with automatic retry mechanism if URL is not available
	 */
	private async setupStreamWithRetry(streamData: StreamData): Promise<void> {
		if (!streamData) {
			return;
		}

		const playbackUrl = streamData.hlsPlaybackUrl;

		// If no playback URL provided, show fallback immediately
		if (!playbackUrl) {
			this.handleUnavailableStream(streamData);
			return;
		}

		// Check if URL is available
		const isAvailable = await this.checkUrlAvailability(playbackUrl);

		if (isAvailable) {
			// URL is ready, configure and play
			this.showStreamUnavailableMessage.set(false);
			this.isRetryingUrl.set(false);
			this.setStreamPlayerConfig(streamData);
		} else {
			// URL not available, show fallback and start retry
			this.handleUnavailableStream(streamData);
			this.startUrlRetry(streamData);
		}
	}

	/**
	 * Checks if a URL is available by making a HEAD request
	 */
	private async checkUrlAvailability(url: string): Promise<boolean> {
		if (!url || this.isComponentDestroyed) {
			return false;
		}

		try {
			const controller = new AbortController();
			const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

			const response = await fetch(url, {
				method: 'HEAD',
				signal: controller.signal,
				cache: 'no-cache',
			});

			clearTimeout(timeoutId);
			return response.ok; // Returns true for 200-299 status codes
		} catch (error) {
			// URL not available or network error
			return false;
		}
	}

	/**
	 * Starts the retry mechanism to periodically check URL availability
	 */
	private startUrlRetry(streamData: StreamData): void {
		// Clear any existing retry
		this.stopUrlRetry();

		this.retryAttempts = 0;
		this.isRetryingUrl.set(true);

		this.retryIntervalId = setInterval(async () => {
			if (this.isComponentDestroyed) {
				this.stopUrlRetry();
				return;
			}

			this.retryAttempts++;

			const playbackUrl = streamData.hlsPlaybackUrl;
			const isAvailable = await this.checkUrlAvailability(playbackUrl);

			if (isAvailable) {
				console.log(
					`Stream URL became available after ${this.retryAttempts} attempts: ${streamData.id}`,
				);
				this.stopUrlRetry();
				this.showStreamUnavailableMessage.set(false);
				this.isRetryingUrl.set(false);
				this.setStreamPlayerConfig(streamData);
				this.cdr.detectChanges();
			}
		}, this.RETRY_INTERVAL_MS);
	}

	/**
	 * Stops the retry mechanism
	 */
	private stopUrlRetry(): void {
		if (this.retryIntervalId) {
			clearInterval(this.retryIntervalId);
			this.retryIntervalId = null;
		}
		this.retryAttempts = 0;
		this.isRetryingUrl.set(false);
	}

	/**
	 * Handles the case when stream URL is unavailable
	 * Shows promotional video only when race is not active; otherwise shows unavailable message
	 */
	private handleUnavailableStream(streamData: StreamData): void {
		const hasPromoVideo = !!streamData?.hlsPromotionalVideoUrl;
		const shouldShowUnavailableMessage =
			this.isMainPlayer && this.raceHappening() && !this.isMockDataType;

		if (hasPromoVideo && !shouldShowUnavailableMessage) {
			// Show promotional video while retrying in background
			this.showStreamUnavailableMessage.set(false);
			const tempData = { ...streamData, hlsPlaybackUrl: streamData.hlsPromotionalVideoUrl };
			this.setStreamPlayerConfig(tempData);
		} else {
			// Show unavailable message
			this.showStreamUnavailableMessage.set(true);
			this.streamPlayerConfig = null;
			this.cdr.detectChanges();
		}
	}
}
